import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import { useNicknames } from '../lib/useNicknames.js';

interface ProductReview {
  asin: string;
  sku: string;
  title: string;
  marketplace: string;
  reviewCount: number;
  avgRating: number;
  reviewCountPrev: number;
  avgRatingPrev: number;
  newReviews: number;
}

interface BuyerReview {
  id: string;
  asin: string;
  marketplace: string;
  review_id: string;
  reviewer_name: string;
  rating: number;
  title: string;
  body: string;
  review_date: string;
  verified_purchase: number;
  is_vine: number;
  helpful_count: number;
  review_images: string | null;
  review_url: string | null;
  user_id: string | null;
  user_profile_url: string | null;
}

interface BuyerReviewsResponse {
  reviews: BuyerReview[];
  total: number;
}

interface SellerFeedback {
  id: string;
  feedbackDate: string;
  rating: number;
  comments: string;
  orderId: string;
  marketplace: string;
  buyerName: string;
}

interface ReviewSummary {
  totalProducts: number;
  avgRating: number;
  totalReviews: number;
  newReviewsThisWeek: number;
  feedbackRating: number;
  feedbackCount: number;
}

const MARKETPLACE_NAMES: Record<string, string> = {
  ATVPDKIKX0DER: 'US', A2EUQ1WTGCTBG2: 'CA', A1AM78C64UM0Y8: 'MX',
  A1F83G8C2ARO7P: 'UK', A1PA6795UKMFR9: 'DE', A13V1IB3VIYZZH: 'FR',
  APJ6JRA9NG5V4: 'IT', A1RKKUPIHCS9HS: 'ES', A1805IZSGTT6HS: 'NL',
  A2NODRKZP88ZB9: 'SE', A1C3SOZF4HXIYN: 'PL', A1S75YUIWTBMNN: 'BE',
  US: 'US', CA: 'CA', MX: 'MX', UK: 'UK', DE: 'DE', FR: 'FR',
  IT: 'IT', ES: 'ES', NL: 'NL', SE: 'SE', PL: 'PL', BE: 'BE',
};

/** Group amzn.gr.* gradation SKUs under their parent. */
function groupGradationProducts(products: ProductReview[]): ProductReview[] {
  const grouped = new Map<string, ProductReview>();

  for (const p of products) {
    // amzn.gr.<parentSku>-<hash> → skip (parent row with same ASIN+marketplace wins)
    if (p.sku?.startsWith('amzn.gr.')) continue;

    const key = `${p.asin}:${p.marketplace}`;
    const existing = grouped.get(key);
    if (!existing || p.reviewCount > existing.reviewCount) {
      grouped.set(key, p);
    }
  }

  return Array.from(grouped.values());
}

function StarRating({ rating, size = 'sm' }: { rating: number; size?: 'sm' | 'lg' }) {
  const full = Math.floor(rating);
  const half = rating - full >= 0.25;
  const textSize = size === 'lg' ? 'text-lg' : 'text-xs';
  return (
    <span className={`${textSize} text-yellow-500`}>
      {'★'.repeat(full)}{half ? '½' : ''}{'☆'.repeat(5 - full - (half ? 1 : 0))}
      <span className="ml-1 text-gray-600">{rating.toFixed(1)}</span>
    </span>
  );
}

export function ProductReviews() {
  const [tab, setTab] = useState<'products' | 'buyer-reviews' | 'feedback' | 'ai-insights'>('products');
  const [products, setProducts] = useState<ProductReview[]>([]);
  const [feedback, setFeedback] = useState<SellerFeedback[]>([]);
  const [buyerReviews, setBuyerReviews] = useState<BuyerReview[]>([]);
  const [buyerReviewsTotal, setBuyerReviewsTotal] = useState(0);
  const [reviewAsinFilter, setReviewAsinFilter] = useState('');
  const [summary, setSummary] = useState<ReviewSummary | null>(null);
  const [aiInsights, setAiInsights] = useState<Array<{
    asin: string; marketplace: string; reviewCount: number; avgRating: number;
    title: string | null; imageUrl: string | null;
    topPraised: string[]; commonComplaints: string[]; featureRequests: string[]; qualityIssues: string[];
    competitiveInsights: string; summary: string; sentimentScore: number; analyzedAt: string;
  }>>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (tab === 'buyer-reviews') loadBuyerReviews();
    if (tab === 'ai-insights' && aiInsights.length === 0) {
      api.get<{ analyses: typeof aiInsights }>('/reviews/analysis')
        .then(d => setAiInsights(d.analyses))
        .catch(() => {});
    }
  }, [tab, reviewAsinFilter]);

  async function loadData() {
    setLoading(true);
    try {
      const [prodData, fbData, summaryData] = await Promise.all([
        api.get<ProductReview[]>('/reviews/products'),
        api.get<SellerFeedback[]>('/reviews/feedback'),
        api.get<ReviewSummary>('/reviews/summary'),
      ]);
      setProducts(prodData);
      setFeedback(fbData);
      setSummary(summaryData);
    } catch {
      // endpoints may not exist yet
    }
    setLoading(false);
  }

  async function loadBuyerReviews() {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (reviewAsinFilter) params.set('asin', reviewAsinFilter);
      const data = await api.get<BuyerReviewsResponse>(`/reviews/buyer-reviews?${params}`);
      setBuyerReviews(data.reviews);
      setBuyerReviewsTotal(data.total);
    } catch {
      // endpoint may not exist yet
    }
    setLoading(false);
  }

  async function enrichReviews() {
    setLoading(true);
    try {
      await api.post('/reviews/products/enrich');
      await loadData();
      if (tab === 'buyer-reviews') await loadBuyerReviews();
    } catch {
      setLoading(false);
    }
  }

  async function collectReviews() {
    setLoading(true);
    try {
      await api.post('/reviews/products/collect-reviews');
      await loadData();
      if (tab === 'buyer-reviews') await loadBuyerReviews();
    } catch {
      setLoading(false);
    }
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
        <SummaryCard label="Products Tracked" value={String(summary?.totalProducts ?? 0)} />
        <SummaryCard label="Avg Rating" value={summary?.avgRating ? summary.avgRating.toFixed(1) : '-'} />
        <SummaryCard label="Total Reviews" value={String(summary?.totalReviews ?? 0)} />
        <SummaryCard
          label="Seller Feedback"
          value={summary?.feedbackCount ? `${summary.feedbackRating.toFixed(1)} (${summary.feedbackCount})` : '-'}
        />
      </div>

      {/* Tab Selector */}
      <div className="flex items-center gap-2 border-b border-gray-200">
        <button
          onClick={() => setTab('products')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            tab === 'products' ? 'border-brand-600 text-brand-700' : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Product Reviews
        </button>
        <button
          onClick={() => setTab('buyer-reviews')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            tab === 'buyer-reviews' ? 'border-brand-600 text-brand-700' : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Buyer Reviews
        </button>
        <button
          onClick={() => setTab('feedback')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            tab === 'feedback' ? 'border-brand-600 text-brand-700' : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Seller Feedback
        </button>
        <button
          onClick={() => setTab('ai-insights')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            tab === 'ai-insights' ? 'border-brand-600 text-brand-700' : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          AI Insights
        </button>
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={() => collectReviews()}
            disabled={loading}
            className="rounded-lg border border-green-300 bg-green-50 px-3 py-1.5 text-xs font-medium text-green-700 hover:bg-green-100 disabled:opacity-50"
          >
            Collect Reviews
          </button>
          <button
            onClick={() => enrichReviews()}
            disabled={loading}
            className="rounded-lg border border-brand-300 bg-brand-50 px-3 py-1.5 text-xs font-medium text-brand-700 hover:bg-brand-100 disabled:opacity-50"
          >
            Refresh Reviews
          </button>
        </div>
      </div>

      {loading ? (
        <div className="h-64 animate-pulse rounded-xl bg-gray-200" />
      ) : tab === 'products' ? (
        <ProductReviewsTable products={products} onViewReviews={(asin) => { setReviewAsinFilter(asin); setTab('buyer-reviews'); }} />
      ) : tab === 'buyer-reviews' ? (
        <BuyerReviewsTable
          reviews={buyerReviews}
          total={buyerReviewsTotal}
          asinFilter={reviewAsinFilter}
          onClearFilter={() => setReviewAsinFilter('')}
        />
      ) : tab === 'ai-insights' ? (
        <AiInsightsPanel insights={aiInsights} products={products} />
      ) : (
        <SellerFeedbackTable feedback={feedback} />
      )}
    </div>
  );
}

function AiInsightsPanel({ insights, products }: { products: ProductReview[]; insights: Array<{
  asin: string; marketplace: string; reviewCount: number; avgRating: number;
  title: string | null; imageUrl: string | null;
  topPraised: string[]; commonComplaints: string[]; featureRequests: string[]; qualityIssues: string[];
  competitiveInsights: string; summary: string; sentimentScore: number; analyzedAt: string;
}> }) {
  const { nickname, parentNickname } = useNicknames();
  const MARKET_LABELS: Record<string, string> = {
    ATVPDKIKX0DER: 'US', A2EUQ1WTGCTBG2: 'CA', A1F83G8C2ARO7P: 'UK',
  };
  // ASIN → first SKU lookup
  const asinToSku = new Map<string, string>();
  for (const p of products) {
    if (p.asin && p.sku && !asinToSku.has(p.asin)) asinToSku.set(p.asin, p.sku);
  }

  if (insights.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-gray-300 p-8 text-center text-sm text-gray-400">
        No AI analysis yet. Reviews are analyzed weekly (Wednesday) or trigger manually.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {insights.map((a, i) => {
        const sentimentColor = a.sentimentScore >= 0.8 ? 'text-green-600' : a.sentimentScore >= 0.6 ? 'text-yellow-600' : 'text-red-600';
        const sentimentBg = a.sentimentScore >= 0.8 ? 'bg-green-50 border-green-200' : a.sentimentScore >= 0.6 ? 'bg-yellow-50 border-yellow-200' : 'bg-red-50 border-red-200';
        return (
          <div key={`${a.asin}-${a.marketplace}-${i}`} className="rounded-xl border border-gray-200 bg-white p-5 space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    {(() => {
                      const sku = asinToSku.get(a.asin);
                      const pn = sku ? parentNickname(sku) : null;
                      const cn = sku ? nickname(sku) : null;
                      const label = pn && cn && cn !== pn ? `${pn} — ${cn}` : pn ?? cn ?? null;
                      return label
                        ? <span className="text-sm font-medium">{label}</span>
                        : <span className="font-mono text-sm font-medium">{a.asin}</span>;
                    })()}
                    <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500">
                      {MARKET_LABELS[a.marketplace] ?? a.marketplace}
                    </span>
                    <span className="text-xs text-gray-400">{a.reviewCount} reviews</span>
                    <span className="text-xs text-gray-400">avg {a.avgRating}/5</span>
                  </div>
                </div>
              </div>
              <div className={`rounded-lg border px-3 py-1.5 text-center ${sentimentBg}`}>
                <p className={`text-lg font-bold ${sentimentColor}`}>{Math.round(a.sentimentScore * 100)}%</p>
                <p className="text-[10px] text-gray-500">sentiment</p>
              </div>
            </div>

            {/* Summary */}
            <p className="text-sm text-gray-700">{a.summary}</p>

            {/* Insights grid */}
            <div className="grid gap-3 sm:grid-cols-2">
              {a.topPraised.length > 0 && (
                <InsightList title="What Customers Love" items={a.topPraised} color="green" />
              )}
              {a.commonComplaints.length > 0 && (
                <InsightList title="Common Complaints" items={a.commonComplaints} color="red" />
              )}
              {a.featureRequests.length > 0 && (
                <InsightList title="Feature Requests" items={a.featureRequests} color="blue" />
              )}
              {a.qualityIssues.length > 0 && (
                <InsightList title="Quality Issues" items={a.qualityIssues} color="orange" />
              )}
            </div>

            {/* Competitive insights */}
            {a.competitiveInsights && (
              <div className="rounded-lg border border-purple-100 bg-purple-50/50 p-3">
                <p className="text-xs font-medium text-purple-700 mb-1">Competitive Positioning</p>
                <p className="text-xs text-purple-600">{a.competitiveInsights}</p>
              </div>
            )}

            <p className="text-[10px] text-gray-300">Analyzed: {new Date(a.analyzedAt).toLocaleDateString()}</p>
          </div>
        );
      })}
    </div>
  );
}

function InsightList({ title, items, color }: { title: string; items: string[]; color: string }) {
  const colors: Record<string, { bg: string; text: string; dot: string }> = {
    green: { bg: 'bg-green-50', text: 'text-green-700', dot: 'bg-green-400' },
    red: { bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-400' },
    blue: { bg: 'bg-blue-50', text: 'text-blue-700', dot: 'bg-blue-400' },
    orange: { bg: 'bg-orange-50', text: 'text-orange-700', dot: 'bg-orange-400' },
  };
  const c = colors[color] ?? colors.blue;
  return (
    <div className={`rounded-lg ${c.bg} p-3`}>
      <p className={`text-xs font-semibold ${c.text} mb-1.5`}>{title}</p>
      <ul className="space-y-1">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-1.5 text-xs text-gray-700">
            <span className={`mt-1.5 h-1.5 w-1.5 rounded-full ${c.dot} flex-shrink-0`} />
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

function ProductReviewsTable({ products, onViewReviews }: { products: ProductReview[]; onViewReviews: (asin: string) => void }) {
  const { displayName, parentNickname } = useNicknames();
  const grouped = groupGradationProducts(products);

  if (grouped.length === 0) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-sm text-gray-400">
        No product review data yet. Review counts will appear after catalog enrichment.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
      <table className="min-w-full divide-y divide-gray-100">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Product</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">ASIN</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Mkt</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Rating</th>
            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">Reviews</th>
            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">New</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {grouped.map((p) => (
            <tr key={`${p.asin}-${p.marketplace}`} className="hover:bg-gray-50">
              <td className="px-4 py-3 text-sm">
                <div className="max-w-[250px]" title={p.sku || p.title}>
                  {p.sku && parentNickname(p.sku) && <span className="text-[10px] text-gray-400 block">{parentNickname(p.sku)}</span>}
                  <span className="truncate block">{p.sku ? displayName(p.sku) : (p.title || '-')}</span>
                </div>
              </td>
              <td className="px-4 py-3 text-sm font-mono text-gray-600">{p.asin}</td>
              <td className="px-4 py-3 text-xs">{MARKETPLACE_NAMES[p.marketplace] ?? p.marketplace}</td>
              <td className="px-4 py-3">
                {p.avgRating > 0 ? <StarRating rating={p.avgRating} /> : <span className="text-xs text-gray-400">-</span>}
              </td>
              <td className="px-4 py-3 text-sm text-right font-medium">
                {p.reviewCount ? (
                  <button onClick={() => onViewReviews(p.asin)} className="text-brand-600 hover:underline">{p.reviewCount}</button>
                ) : '-'}
              </td>
              <td className="px-4 py-3 text-right">
                {p.newReviews > 0 ? (
                  <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">+{p.newReviews}</span>
                ) : (
                  <span className="text-xs text-gray-400">-</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BuyerReviewsTable({ reviews, total, asinFilter, onClearFilter }: {
  reviews: BuyerReview[];
  total: number;
  asinFilter: string;
  onClearFilter: () => void;
}) {
  if (reviews.length === 0) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-sm text-gray-400">
        {asinFilter ? (
          <>No buyer reviews found for ASIN {asinFilter}. <button onClick={onClearFilter} className="text-brand-600 hover:underline">Show all</button></>
        ) : (
          'No buyer reviews stored yet. Click "Refresh Reviews" to scrape reviews from Amazon product pages.'
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {asinFilter && (
        <div className="flex items-center gap-2 text-sm">
          <span className="text-gray-500">Filtered by ASIN:</span>
          <span className="rounded bg-gray-100 px-2 py-0.5 font-mono text-xs">{asinFilter}</span>
          <button onClick={onClearFilter} className="text-brand-600 hover:underline text-xs">Clear</button>
          <span className="ml-auto text-xs text-gray-400">{total} review{total !== 1 ? 's' : ''} total</span>
        </div>
      )}
      {!asinFilter && (
        <div className="text-xs text-gray-400 text-right">{total} review{total !== 1 ? 's' : ''} total</div>
      )}
      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-100">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Rating</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Title</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Review</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Reviewer</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Date</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">ASIN</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Mkt</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {reviews.map((r) => (
              <tr key={r.id} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                    r.rating >= 4 ? 'bg-green-100 text-green-700' :
                    r.rating === 3 ? 'bg-yellow-100 text-yellow-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {'★'.repeat(r.rating)}{'☆'.repeat(5 - r.rating)}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm font-medium">
                  <div className="max-w-[200px] truncate" title={r.title}>
                    {r.review_url ? (
                      <a href={r.review_url} target="_blank" rel="noopener noreferrer" className="hover:text-brand-600 hover:underline">{r.title || '-'}</a>
                    ) : (r.title || '-')}
                  </div>
                  <div className="flex flex-wrap gap-1 mt-0.5">
                  {r.verified_purchase ? (
                    <span className="text-[10px] text-green-600">Verified</span>
                  ) : null}
                  {r.is_vine ? (
                    <span className="text-[10px] text-orange-600">Vine</span>
                  ) : null}
                  {r.review_images && JSON.parse(r.review_images).length > 0 ? (
                    <span className="text-[10px] text-blue-500">{JSON.parse(r.review_images).length} img</span>
                  ) : null}
                </div>
                </td>
                <td className="px-4 py-3 text-sm">
                  <div className="max-w-[350px] line-clamp-2 text-gray-600" title={r.body}>{r.body || '-'}</div>
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">
                  {r.user_profile_url ? (
                    <a href={r.user_profile_url} target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline text-xs font-mono" title={r.user_id || undefined}>
                      {r.user_id ? `${r.user_id.slice(0, 6)}...${r.user_id.slice(-4)}` : 'Profile'}
                    </a>
                  ) : (r.reviewer_name !== 'Anonymous' ? r.reviewer_name : '-')}
                </td>
                <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{r.review_date || '-'}</td>
                <td className="px-4 py-3 text-sm font-mono text-gray-600">{r.asin}</td>
                <td className="px-4 py-3 text-xs">{MARKETPLACE_NAMES[r.marketplace] ?? r.marketplace}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SellerFeedbackTable({ feedback }: { feedback: SellerFeedback[] }) {
  if (feedback.length === 0) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-sm text-gray-400">
        No seller feedback data. The GET_SELLER_FEEDBACK_DATA report requires Selling Partner Insights role.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
      <table className="min-w-full divide-y divide-gray-100">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Date</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Rating</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Comments</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Order</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Mkt</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {feedback.map((f) => (
            <tr key={f.id} className="hover:bg-gray-50">
              <td className="px-4 py-3 text-sm text-gray-600">{f.feedbackDate}</td>
              <td className="px-4 py-3">
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                  f.rating >= 4 ? 'bg-green-100 text-green-700' :
                  f.rating === 3 ? 'bg-yellow-100 text-yellow-700' :
                  'bg-red-100 text-red-700'
                }`}>
                  {'★'.repeat(f.rating)}{'☆'.repeat(5 - f.rating)}
                </span>
              </td>
              <td className="px-4 py-3 text-sm">
                <div className="max-w-[400px] truncate" title={f.comments}>{f.comments || '-'}</div>
              </td>
              <td className="px-4 py-3 text-sm font-mono text-gray-600">{f.orderId || '-'}</td>
              <td className="px-4 py-3 text-xs">{MARKETPLACE_NAMES[f.marketplace] ?? f.marketplace}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SummaryCard({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <p className="text-xs font-medium text-gray-500">{label}</p>
      <p className={`mt-1 text-2xl font-bold ${color ?? ''}`}>{value}</p>
    </div>
  );
}
